import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Context } from '@service/context.service';
import { Mandant } from '@entity/mandant';
import { AnwenderbetriebService } from '@service/anwenderbetrieb.service';
import { AbstractContentComponent } from '../abstract-content.component';
import { Subscription } from 'rxjs';
import { Column } from '@shared/table/column';
import { MandantService } from '@service/mandant.service';
import { SharedModule } from 'primeng/api';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { DeletionJobService } from '@service/deletion-job.service';

@Component({
  selector: 'app-mandants',
  templateUrl: './mandants.component.html',
  styleUrls: ['./mandants.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, ToolbarButtonDirective, ActionButtonDirective, SharedModule],
})
export class MandantsComponent extends AbstractContentComponent implements OnInit, OnDestroy {
  private mandantService = inject(MandantService);
  private anwenderbetriebService = inject(AnwenderbetriebService);
  private deletionJobService = inject(DeletionJobService);

  private completedSub?: Subscription;
  private currentAnwenderbetriebId: number | null = null;

  columns: Column[] = [];
  mandants: Mandant[] = [];
  canDelete = false;

  constructor() {
    super(Context.Anwenderbetrieb, 'MENU.MANDANTS');
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.canDelete = this.isRodixAdmin();

    this.completedSub = this.deletionJobService.completed$.subscribe(() => {
      if (this.currentAnwenderbetriebId != null) {
        this.onAnwenderbetriebIdChange(this.currentAnwenderbetriebId);
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.completedSub?.unsubscribe();
  }

  delete(mandant: Mandant): void {
    this.confirm({
      header: this.translate('ANWENDERBETRIEBE.DELETE_HEADER'),
      message: this.translate('ANWENDERBETRIEBE.DELETE_MESSAGE', {
        id: mandant.mandantId,
        name: mandant.bezeichnung,
      }),
      accept: () => {
        this.deleteMandant(mandant, false);
      },
    });
  }

  deleteDataOnly(mandant: Mandant): void {
    this.confirm({
      header: this.translate('ANWENDERBETRIEBE.DELETE_DATA_ONLY_HEADER'),
      message: this.translate('ANWENDERBETRIEBE.DELETE_DATA_ONLY_MESSAGE', {
        id: mandant.mandantId,
        name: mandant.bezeichnung,
      }),
      accept: () => {
        this.deleteMandant(mandant, true);
      },
    });
  }

  add(): void {
    this.navigate(['new']);
  }

  protected onLanguageChange(): void {
    this.setColumns();
  }

  protected onAnwenderbetriebIdChange(id: number | null): void {
    this.currentAnwenderbetriebId = id;
    if (id == null) {
      this.mandants = [];
      this.markForCheck();
      return;
    }
    this.startLoading();
    this.anwenderbetriebService.getMandants(id).subscribe((data) => {
      this.mandants = data;
      this.markDeletingRows();
      this.markForCheck();
      this.loadingDone();
    });
  }

  private setColumns(): void {
    this.columns = [
      {
        field: 'mandantId',
        headerKey: 'GLOBAL.ID',
        width: '120px',
        route: '/mandants/${id}',
      },
      {
        field: 'acronym',
        headerKey: 'GLOBAL.ACRONYM',
        width: '150px',
      },
      {
        field: 'bezeichnung',
        headerKey: 'GLOBAL.NAME',
        minWidth: '250px',
      },
      {
        field: 'tacsRecordingUrl',
        headerKey: 'ANWENDERBETRIEBE.TACS_RECORDING_URL',
        minWidth: '250px',
        url: 'tacsRecordingUrl',
      },
      {
        field: 'gueltigAb',
        headerKey: 'GLOBAL.GUELTIG_AB',
        width: '130px',
        filterType: 'date',
      },
      {
        field: 'gueltigBis',
        headerKey: 'GLOBAL.GUELTIG_BIS',
        width: '130px',
        filterType: 'date',
      },
      {
        field: 'benchmarkFreigabe',
        headerKey: 'MANDANTS.BENCHMARK',
        width: '130px',
        filterType: 'boolean',
      },
      {
        field: 'logoWeb',
        headerKey: 'MANDANTS.LOGO',
        width: '120px',
        filterType: 'boolean',
      },
      {
        field: 'superUsers',
        headerKey: 'MANDANTS.SUPERUSERS',
        width: '400px',
      },
    ];
  }

  private deleteMandant(mandant: Mandant, dataOnly: boolean): void {
    this.mandantService.deleteMandant(mandant.id, dataOnly).subscribe({
      next: (result) => {
        (mandant as Mandant & { deleting: boolean }).deleting = true;
        this.mandants = [...this.mandants];
        this.markForCheck();

        const key = dataOnly ? 'ANWENDERBETRIEBE.DELETE_DATA_ONLY_STARTED' : 'ANWENDERBETRIEBE.DELETE_STARTED';
        this.showSuccessMessage(
          this.translate(key, {
            id: mandant.mandantId,
            name: mandant.bezeichnung,
          })
        );

        this.deletionJobService.startPolling(result.deletionJobId);
      },
      error: () => {
        const key = dataOnly ? 'ANWENDERBETRIEBE.DELETE_DATA_ONLY_FAILED' : 'ANWENDERBETRIEBE.DELETE_FAILED';
        this.showErrorMessage(
          this.translate(key, {
            id: mandant.mandantId,
            name: mandant.bezeichnung,
          })
        );
      },
    });
  }

  private markDeletingRows(): void {
    for (const mandant of this.mandants) {
      this.deletionJobService
        .isDeleting('Mandant', mandant.id)
        .subscribe((deleting) => {
          if (deleting) {
            (mandant as Mandant & { deleting: boolean }).deleting = true;
          }
        })
        .unsubscribe();
    }
  }
}
